import { useState } from 'react'
import { Activity, RefreshCw, ChevronDown, ChevronRight, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useTaskStatus } from '@/hooks/useTaskStatus'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'
import { groupMetaTasks } from './task-status-meta'

// 状态映射
const STATUS_MAP = {
  0: { text: '待执行', color: 'text-gray-500' },
  1: { text: '执行中', color: 'text-blue-500' },
  2: { text: '执行结束', color: 'text-green-500' },
  3: { text: '执行失败', color: 'text-red-500' },
  4: { text: '数据未更新', color: 'text-yellow-500' },
}

// 获取状态颜色（用于图标）
const getStatusColor = (status) => {
  const colorMap = {
    0: 'text-gray-500',
    1: 'text-blue-500',
    2: 'text-green-500',
    3: 'text-red-500',
    4: 'text-yellow-500',
  }
  return colorMap[status] || 'text-gray-500'
}

// 获取最严重的状态（用于图标颜色）
// 考虑主任务和子任务的状态
const getMostSevereStatus = (tasks) => {
  if (!tasks || tasks.length === 0) return null

  // 收集所有任务状态（包括主任务和子任务）
  const allStatuses = []
  tasks.forEach((task) => {
    allStatuses.push(task.status)
    if (task.freshness?.state === 'stale') allStatuses.push(4)
    if (task.children && task.children.length > 0) {
      task.children.forEach((child) => {
        allStatuses.push(child.status)
        if (child.freshness?.state === 'stale') allStatuses.push(4)
      })
    }
  })

  // 优先级：失败(3) > 数据未更新(4) > 执行中(1) > 待执行(0) > 执行结束(2)
  const priority = [3, 4, 1, 0, 2]
  for (const p of priority) {
    if (allStatuses.includes(p)) return p
  }
  return allStatuses[0] || null
}

// 检查是否有需要警示的任务（执行失败或数据未更新）
// 考虑主任务和子任务的状态
const hasAlertTasks = (tasks) => {
  if (!tasks || tasks.length === 0) return false
  // 需要警示的状态：3=执行失败, 4=数据未更新
  const alertStatuses = [3, 4]

  return tasks.some((task) => {
    // 检查主任务状态
    if (alertStatuses.includes(task.status) || task.freshness?.state === 'stale') return true

    // 检查子任务状态
    if (task.children && task.children.length > 0) {
      return task.children.some((child) => alertStatuses.includes(child.status) || child.freshness?.state === 'stale')
    }

    return false
  })
}

// 格式化时间
const formatTime = (date) => {
  if (!date) return '-'
  try {
    const time = dayjs(date)
    // 检查日期是否有效
    if (!time.isValid()) return '-'
    return new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(time.toDate())
  } catch (error) {
    console.error('日期格式化错误:', error)
    return '-'
  }
}

// 格式化相对时间（根据状态显示不同的文案）
// status: 任务状态，0=待执行, 1=执行中, 2=执行结束, 3=执行失败, 4=数据未更新
// suffix: 后缀文案，'start'=开始执行, 'success'=成功, 'complete'=执行结束, 'update'=更新（默认）
const formatRelativeTime = (date, status = null, suffix = 'update') => {
  if (!date) return '-'
  try {
    // 将日期字符串解析为dayjs对象
    const time = dayjs(date)
    // 检查日期是否有效
    if (!time.isValid()) return '-'

    // 获取当前时间
    const now = dayjs()
    const diffMinutes = now.diff(time, 'minute')

    if (diffMinutes < 0) {
      // 如果时间在未来，返回原始格式
      return time.format('MM-DD HH:mm')
    }

    // 根据状态确定后缀文案
    let suffixText = suffix === 'complete' ? '执行结束' : '更新'
    if (status === 1) {
      suffixText = '开始执行'
    } else if (status === 2 || suffix === 'success') {
      suffixText = '成功'
    } else if (suffix === 'start') {
      suffixText = '开始执行'
    }

    if (diffMinutes < 60) {
      return `${diffMinutes}分钟前${suffixText}`
    }

    const hours = Math.floor(diffMinutes / 60)
    const minutes = diffMinutes % 60

    if (minutes === 0) {
      return `${hours}小时前${suffixText}`
    }

    return `${hours}小时${minutes}分钟前${suffixText}`
  } catch (error) {
    console.error('相对时间格式化错误:', error)
    return '-'
  }
}

// 排序任务列表：status==2放到尾部，其余按status倒序排列
const sortTasks = (tasks) => {
  if (!tasks || tasks.length === 0) return []

  const status2Tasks = tasks.filter((task) => task.status === 2)
  const otherTasks = tasks.filter((task) => task.status !== 2)

  // 其他任务按status倒序排列
  otherTasks.sort((a, b) => b.status - a.status)

  // status==2的任务放到尾部
  return [...otherTasks, ...status2Tasks]
}

// 自动执行任务配置（Cloud Scheduler，Asia/Shanghai；与 data-collector/scripts/scheduler/setup-schedulers.sh 一致）
const AUTO_TASKS = [
  {
    name: 'jobs/mysql_ga.js',
    purpose: 'DAU&DNU GA 更新',
    schedule: '每小时的第5分钟',
    description: '每小时执行一次（北京时间）',
    cron: '5 * * * *',
  },
  {
    name: 'jobs/mysql_metabase.js',
    purpose: 'DAU&DNU MetaBase 更新',
    schedule: '每天 09:35',
    description: '每天执行一次（北京时间）',
    cron: '35 9 * * *',
  },
  {
    name: 'jobs/mysql_app.js',
    purpose: 'Cost 更新、Revenue 更新',
    schedule: '08–20 点每小时 :20/:50；21–次日 07 点每小时 :20；03:35 回补',
    description: '跳过 Meta；Pangle 单独每小时 :10/:40、06:40 回补（北京时间）',
    cron: '20,50 8-20 * * *; 20 21-23,0-7 * * *; 35 3 * * *',
  },
  {
    name: 'jobs/meta_refresh.js',
    purpose: 'Meta 成本与收入独立刷新',
    schedule: '全天每 5 分钟巡检，数据约 35–40 分钟刷新',
    description: '未到期跳过；超过 1 小时显示数据未更新（北京时间）',
    cron: '*/5 * * * *',
  },
  {
    name: 'jobs/meta_refresh.js --history',
    purpose: 'Meta 历史回补',
    schedule: '每天 03:37',
    description: '先检查日常新鲜度；失败项最多再重试两次，06:00 后不再发起重试（北京时间）',
    cron: '37 3 * * *',
  },
  {
    name: 'jobs/mysql_app_camps.js',
    purpose: 'Campaign 同步',
    schedule: '08–23 点每小时 :45；夜间仅 00:45、06:45',
    description: '夜间降频，错开 03:37 Meta 历史回补（北京时间）',
    cron: '45 0,6,8-23 * * *',
  },
  {
    name: 'jobs/mysql_app_1.js',
    purpose: 'iOS 订阅更新',
    schedule: '每天 17:04-01:04（每小时）',
    description: '每天17:04, 18:04, ..., 23:04, 00:04, 01:04执行（北京时间）',
    cron: '4 17-23,0-1 * * *',
  },
  {
    name: 'jobs/mysql_app_2.js',
    purpose: 'GP 订阅更新',
    schedule: '每小时的第24分钟',
    description: '每小时执行一次（北京时间）',
    cron: '24 * * * *',
  },
  {
    name: 'jobs/mysql_app_4.js',
    purpose: 'Campaign Cost 更新',
    schedule: '每小时的第5分钟',
    description: '每小时执行一次（北京时间）',
    cron: '5 * * * *',
  },
  {
    name: 'jobs/mysql_admob.js',
    purpose: 'AdMob 更新',
    schedule: '每小时的第50分钟',
    description: '每小时执行一次（北京时间）',
    cron: '50 * * * *',
  },
  {
    name: 'jobs/mysql_roas_update.js (auto模式)',
    purpose: 'ROAS数据更新',
    schedule: '每天 16:45',
    description: '每天执行一次，仅上游数据写入完成后才执行（北京时间）',
    cron: '45 16 * * *',
  },
]

export function TaskStatusIcon() {
  const { tasks, isLoading, isValidating, isError, refresh } = useTaskStatus()
  // 用于跟踪哪些主任务已展开
  const [expandedTasks, setExpandedTasks] = useState(new Set())

  // 排序任务列表：status==2放到尾部，其余按status倒序排列
  const groupedTasks = groupMetaTasks(tasks)
  const sortedTasks = sortTasks(groupedTasks)

  // 获取图标颜色
  const iconStatus = getMostSevereStatus(groupedTasks)
  const iconColor =
    iconStatus !== null ? getStatusColor(iconStatus) : 'text-gray-400'

  // 检查是否有需要警示的任务
  const showAlert = hasAlertTasks(groupedTasks)

  const handleRefresh = () => {
    refresh()
  }

  const isRefreshing = isLoading || isValidating

  // 切换任务展开/收起状态
  const toggleTask = (taskKey) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(taskKey)) {
        newSet.delete(taskKey)
      } else {
        newSet.add(taskKey)
      }
      return newSet
    })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" aria-label="查看任务状态">
          <Activity className={cn('h-4 w-4', iconColor)} />
          {showAlert && (
            <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full animate-pulse" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-h-[min(80vh,var(--radix-popover-content-available-height))] overflow-y-auto" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div className="font-semibold text-sm">任务状态</div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>自动执行任务信息</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-4">
                    <div className="text-sm text-muted-foreground mb-4">
                      以下任务由服务器定时自动执行（显示时间为北京时间）：
                    </div>
                    <div className="space-y-3">
                      {AUTO_TASKS.map((task, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-md border border-border/40 bg-accent/30"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-primary mb-1.5">
                                {task.purpose}
                              </div>
                              <div className="text-xs text-muted-foreground mb-1">
                                {task.description}
                              </div>
                              <div className="text-xs font-mono text-muted-foreground">
                                执行时间：{task.schedule}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              aria-label="刷新任务状态"
              disabled={isRefreshing}
              className="h-7 w-7 p-0"
            >
              <RefreshCw
                className={cn('h-4 w-4', isRefreshing && 'animate-spin')}
              />
            </Button>
          </div>
          {isRefreshing ? (
            <div className="text-sm text-muted-foreground">加载中...</div>
          ) : isError ? (
            <div className="text-sm text-red-500">加载失败，请重试</div>
          ) : sortedTasks.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无任务</div>
          ) : (
            <div className="space-y-2">
              {sortedTasks.map((task) => {
                const statusInfo = STATUS_MAP[task.status] || STATUS_MAP[0]
                const displayName = task.taskName || task.taskType || task.task
                const taskKey = task.task
                const hasChildren = task.children && task.children.length > 0
                const canExpand = hasChildren
                const isExpanded = expandedTasks.has(taskKey)
                const childStatusCount = task.childStatusCount

                return (
                  <div key={taskKey}>
                    {/* 主任务 */}
                    <div
                      className={cn(
                        'flex items-start justify-between p-2 rounded-md border border-border/40 hover:bg-accent/50 transition-colors',
                        canExpand && 'cursor-pointer',
                      )}
                      role={canExpand ? 'button' : undefined}
                      tabIndex={canExpand ? 0 : undefined}
                      aria-label={canExpand ? `${isExpanded ? '收起' : '展开'}${displayName}` : undefined}
                      aria-expanded={canExpand ? isExpanded : undefined}
                      onClick={() => canExpand && toggleTask(taskKey)}
                      onKeyDown={(event) => {
                        if (canExpand && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault()
                          toggleTask(taskKey)
                        }
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {canExpand && (
                              <div className="flex-shrink-0">
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                )}
                              </div>
                            )}
                            <div className="text-sm font-medium truncate">
                              {displayName}
                            </div>
                            {childStatusCount && (
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                ({childStatusCount.success}/
                                {childStatusCount.total})
                              </span>
                            )}
                          </div>
                          <span
                            className={cn(
                              'text-xs whitespace-nowrap',
                              statusInfo.color,
                            )}
                          >
                            {statusInfo.text}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <div className="text-xs text-muted-foreground">
                            {task.completedAt
                              ? formatRelativeTime(
                                  task.completedAt,
                                  task.runStatus ?? task.status,
                                  task.runStatus !== undefined ? 'complete' : 'update',
                                )
                              : task.startAt
                              ? formatRelativeTime(task.startAt, task.status)
                              : '-'}
                          </div>
                          {task.lastSuccessCompletedAt && task.status !== 2 && (
                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatRelativeTime(
                                task.lastSuccessCompletedAt,
                                null,
                                'success',
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* 子任务列表 */}
                    {canExpand && isExpanded && (
                      <div className="ml-4 mt-1 space-y-1.5 border-l-2 border-border/30 pl-3">
                        {(task.children || []).map((child) => {
                          const childStatusInfo =
                            STATUS_MAP[child.status] || STATUS_MAP[0]
                          const childDisplayName =
                            child.taskName || child.taskType || child.task

                          return (
                            <div
                              key={child.task}
                              className="flex items-start justify-between p-1.5 rounded-md border border-border/30 hover:bg-accent/30 transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-xs font-medium truncate">
                                    {childDisplayName}
                                  </div>
                                  <span
                                    className={cn(
                                      'text-xs whitespace-nowrap',
                                      childStatusInfo.color,
                                    )}
                                  >
                                    {childStatusInfo.text}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-2 mt-0.5">
                                  <div className="text-xs text-muted-foreground">
                                    {child.completedAt
                                      ? formatRelativeTime(
                                          child.completedAt,
                                          child.runStatus ?? child.status,
                                          child.runStatus !== undefined ? 'complete' : 'update',
                                        )
                                      : child.startAt
                                      ? formatRelativeTime(
                                          child.startAt,
                                          child.status,
                                        )
                                      : '-'}
                                  </div>
                                </div>
                                {child.status === 3 && ['meta_history_cost', 'meta_history_revenue'].includes(child.taskType) && (
                                  <div className="text-xs text-muted-foreground mt-0.5" title="北京时间">
                                    上次成功：{child.lastSuccessCompletedAt ? formatTime(child.lastSuccessCompletedAt) : '暂无记录'}
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
